import React from 'react';
import { getHSApiConfig } from 'src/constants/config';
import { actions, useCategories, useItems, useTypes } from 'src/data/redux';
import { useLargeDropdownOptions, useConditionalEffect, valueListToOptions } from 'src/commons';
import { useDispatch } from 'react-redux';
import { GetCategories, GetItems, GetTypes } from 'src/lib/hammerstoneApi';
import { Box, Button, SpaceBetween } from '@amzn/awsui-components-console';
import Content, { Rules } from './content/';
import { DisplayMode, ResourcePath, ResourceType, iContent } from './content/contentInterfaces';
import { useFormContext, useWatch, UseFormSetValue } from 'react-hook-form';

interface iCtiContent<RType extends keyof ResourceType, DisableOnPaths extends ResourcePath<RType>[] = []>
	extends Pick<
		iContent<RType, DisableOnPaths>,
		'disabled' | 'disableOn' | 'mode' | 'resourceType' | 'resourceId' | 'rules'
	> {
	path: {
		category: ResourcePath<RType>;
		type: ResourcePath<RType>;
		item: ResourcePath<RType>;
	};
}

/**  A custom content component which will render an input field for Category, Type, and Item while handling logic for fetching and rendering the correct options. */
export function CtiContent<RType extends keyof ResourceType, DisableOnPaths extends ResourcePath<RType>[] = []>(
	props: iCtiContent<RType, DisableOnPaths>
) {
	// The CTI dropdowns are backed by HSAPI (GetCategories/GetTypes/GetItems), which proxies
	// Tickety server-side and resolves the Tickety endpoint per-partition from RIP (e.g.
	// global.api.tickety.a2z.org.cn in aws-cn). Render dropdowns wherever HSAPI is configured
	// for the region; fall back to free-text inputs only where HSAPI is unavailable. This covers
	// classic (aws), China (aws-cn), EUSC (aws-eusc), and any future region with an HSAPI config.
	// "In what AWS regions is Tickety API accessible?" https://w.amazon.com/bin/view/IssueManagement/SIMTicketing/TicketyAPI/FAQ#HService
	const isCtiApiAvailable = Boolean(getHSApiConfig());

	if (isCtiApiAvailable) {
		return <CtiSelectContent {...props} />;
	} else {
		return <CtiTextContent {...props} />;
	}
}

/** Render C,T, and I fields as text inputs if the Tickety API is not available in the current partition */
export function CtiTextContent<RType extends keyof ResourceType, DisableOnPaths extends ResourcePath<RType>[] = []>(
	props: iCtiContent<RType, DisableOnPaths>
) {
	return (
		<SpaceBetween size="m">
			<Content.Text
				{...props}
				path={props.path.category}
				label="Category"
				rules={{ ...props.rules, maxLength: Rules.maxLength(64, 'Category') }}
			/>
			<Content.Text
				{...props}
				path={props.path.type}
				label="Type"
				rules={{ ...props.rules, maxLength: Rules.maxLength(64, 'Type') }}
			/>
			<Content.Text
				{...props}
				path={props.path.item}
				label="Item"
				rules={{ ...props.rules, maxLength: Rules.maxLength(64, 'Item') }}
			/>
		</SpaceBetween>
	);
}

/** Render C,T, and I fields as Select dropdowns if the Tickety API is available and handle the custom logic for fetching C,T,I from API */
export function CtiSelectContent<RType extends keyof ResourceType, DisableOnPaths extends ResourcePath<RType>[] = []>(
	props: iCtiContent<RType, DisableOnPaths>
) {
	const isEditing = props.mode === DisplayMode.Edit;
	const dispatch = useDispatch();

	let category: string;
	let type: string;
	try {
		// Subscribes to the selected category and type fields
		category = useWatch({ name: props.path.category });
		type = useWatch({ name: props.path.type });
	} catch {
		category = '';
		type = '';
	}

	// Subscribes to categories, types, and items from Redux store's CTI Slice
	const categories = useCategories();
	const types = useTypes(category);
	const items = useItems(category, type);

	useConditionalEffect(() => {
		// If editing, fetch categories if they have not yet been loaded
		GetCategories({}).then(({ results }) => {
			if (results?.length) {
				dispatch(actions.cti.setCategories({ categories: results }));
			}
		});
	}, [isEditing, !categories?.length]);

	useConditionalEffect(() => {
		// If editing, fetch types of this category if they are not yet loaded
		GetTypes({ category }).then(({ results }) => {
			if (results?.length) {
				dispatch(actions.cti.setTypes({ category, types: results }));
			}
		});
	}, [isEditing, category, !types?.length]);

	useConditionalEffect(() => {
		// If editing, fetch items of this category and type if they are not yet loaded
		GetItems({ category, type }).then(({ results }) => {
			if (results?.length) {
				dispatch(actions.cti.setItems({ category, type, items: results }));
			}
		});
	}, [isEditing, category, type, !items?.length]);

	const categoryFilterProps = useLargeDropdownOptions(valueListToOptions(categories));
	const typeFilterProps = useLargeDropdownOptions(valueListToOptions(types));
	const itemFilterProps = useLargeDropdownOptions(valueListToOptions(items));

	let setFieldValue: UseFormSetValue<ResourceType[RType]> = () => {};
	try {
		const { setValue } = useFormContext<ResourceType[RType]>();
		setFieldValue = setValue;
	} catch (error) {
		if (props.mode === DisplayMode.Edit) {
			console.error(
				'Failed to retrieve `react-hook-form` context (`useFormContext()`) in `<Content.Cti>`; make sure component is rendered within a `<FormProvider>`'
			);
		}
	}

	return (
		<SpaceBetween size="m">
			<Content.Select
				{...props}
				{...categoryFilterProps}
				label="Category"
				path={props.path.category}
				onChange={({ detail }) => {
					setFieldValue(props.path.category, detail.selectedOption.value);
					setFieldValue(props.path.type, undefined);
					setFieldValue(props.path.item, undefined);
				}}
			/>
			<Content.Select
				{...props}
				{...typeFilterProps}
				label="Type"
				path={props.path.type}
				onChange={({ detail }) => {
					setFieldValue(props.path.type, detail.selectedOption.value);
					setFieldValue(props.path.item, undefined);
				}}
				disabled={props.disabled || !category}
			/>
			<Content.Select
				{...props}
				{...itemFilterProps}
				label="Item"
				path={props.path.item}
				// Disable Item field if no category & type is selected
				disabled={!category || !type || props.disabled}
			/>
			{props.mode === DisplayMode.Edit && (
				<Box float="right" padding={{ top: 'l' }}>
					<Button
						disabled={!category}
						onClick={() => {
							setFieldValue(props.path.category, undefined);
							setFieldValue(props.path.type, undefined);
							setFieldValue(props.path.item, undefined);
						}}
					>
						Clear CTI
					</Button>
				</Box>
			)}
		</SpaceBetween>
	);
}

export default CtiContent;
